// src/utils/turnstileVerification.ts

import { supabase } from '@/integrations/supabase/client';

interface TurnstileVerificationResult {
  success: boolean;
  error?: string;
  errorCodes?: string[];
}

// Server-side Turnstile verification - DISABLED
export const verifyTurnstileToken = async (token: string): Promise<TurnstileVerificationResult> => {
  try {
    console.log('🔐 Starting server-side Turnstile verification... (DISABLED)');
    
    // TURNSTILE DISABLED: Return success immediately
    return { success: true };
    
    /* TURNSTILE CODE COMMENTED OUT:
    // Get client IP if available (best effort)
    const clientIP = await getClientIP();

    const { data, error } = await supabase.functions.invoke('verify-turnstile', {
      body: { token, ip: clientIP }
    });

    if (error) {
      console.error('❌ Turnstile verification failed:', error);
      return {
        success: false,
        error: 'Verification failed. Please try again.'
      };
    }

    console.log('✅ Turnstile verification completed:', data);
    return data;
    */
  } catch (error) {
    console.error('🚨 Turnstile verification error:', error);
    // Return success even on error when disabled
    return { success: true };
  }
};

// Get client IP address (best effort) - COMMENTED OUT
const getClientIP = async (): Promise<string | null> => {
  try {
    // This is a best-effort approach to get the client IP
    // In production, you might want to use a more reliable service
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip || null;
  } catch {
    // Silently fail - IP is optional for Turnstile verification
    return null;
  }
};

// Rate limiting for security
interface RateLimitState {
  attempts: number;
  lastAttempt: number;
  blockedUntil?: number;
}

const RATE_LIMIT_STORAGE_KEY = 'turnstile_rate_limit';
const MAX_ATTEMPTS = 5;
const BLOCK_DURATION = 15 * 60 * 1000; // 15 minutes
const ATTEMPT_WINDOW = 60 * 1000; // 1 minute

export const checkRateLimit = (): { allowed: boolean; remainingTime?: number } => {
  const stored = localStorage.getItem(RATE_LIMIT_STORAGE_KEY);
  const now = Date.now();

  if (!stored) {
    return { allowed: true };
  }

  try {
    const state: RateLimitState = JSON.parse(stored);

    // Check if still blocked
    if (state.blockedUntil && now < state.blockedUntil) {
      return {
        allowed: false,
        remainingTime: Math.ceil((state.blockedUntil - now) / 1000)
      };
    }

    // Reset if outside attempt window
    if (now - state.lastAttempt > ATTEMPT_WINDOW) {
      localStorage.removeItem(RATE_LIMIT_STORAGE_KEY);
      return { allowed: true };
    }

    // Check if too many attempts
    if (state.attempts >= MAX_ATTEMPTS) {
      const blockedUntil = now + BLOCK_DURATION;
      localStorage.setItem(RATE_LIMIT_STORAGE_KEY, JSON.stringify({
        ...state,
        blockedUntil
      }));
      return {
        allowed: false,
        remainingTime: Math.ceil(BLOCK_DURATION / 1000)
      };
    }

    return { allowed: true };
  } catch {
    // Clear corrupted data
    localStorage.removeItem(RATE_LIMIT_STORAGE_KEY);
    return { allowed: true };
  }
};

export const recordAttempt = () => {
  const stored = localStorage.getItem(RATE_LIMIT_STORAGE_KEY);
  const now = Date.now();

  if (!stored) {
    localStorage.setItem(RATE_LIMIT_STORAGE_KEY, JSON.stringify({
      attempts: 1,
      lastAttempt: now
    }));
    return;
  }

  try {
    const state: RateLimitState = JSON.parse(stored);
    localStorage.setItem(RATE_LIMIT_STORAGE_KEY, JSON.stringify({
      attempts: state.attempts + 1,
      lastAttempt: now
    }));
  } catch {
    // Reset on error
    localStorage.setItem(RATE_LIMIT_STORAGE_KEY, JSON.stringify({
      attempts: 1,
      lastAttempt: now
    }));
  }
};